// 五つのテーマ（月・太陽・宇宙天気・夜空・地球）の「ひとこと」と詳細行を組み立て、今日の一つを選ぶ
import type { CosmosData } from './useCosmosData';
import { hhmm } from './sun';
import { auroraChance } from './spaceWeather';
import {
  moonMessage, sekkiMessage, spaceWeatherMessage, NIGHT_SKY_INVITE,
  seasonMessage, doyoMessage, tideMessage,
} from './messages';

export type ThemeKey = 'moon' | 'sun' | 'spaceWeather' | 'nightSky' | 'earth';

export type ThemeItem = {
  key: ThemeKey;
  label: string;         // テーマ名
  icon: string;
  headline: string;      // ひとこと（トップに出す一文）
  lines: string[];       // 詳細（数値など）
  notes: string[];       // 金色で添える補足（節入り・オーロラなど）
  needsLocation: boolean; // 位置がなくて一部を出せていない
  special: boolean;      // 今日が節目にあたる（新月・節入りなど）
};

export const THEME_ORDER: ThemeKey[] = ['moon', 'sun', 'spaceWeather', 'nightSky', 'earth'];

const MOON_ICON: Record<string, string> = {
  new: '🌑', crescent: '🌒', waxing: '🌒', firstQuarter: '🌓', gibbous: '🌔',
  full: '🌕', izayoi: '🌖', waning: '🌖', lastQuarter: '🌗', ariake: '🌘',
};

const sodMs = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const md = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
const daysLabel = (n: number) => (n === 0 ? '今日' : `あと${n}日`);

function dayWord(d: Date, now: Date): string {
  const diff = Math.round((sodMs(d) - sodMs(now)) / 86400000);
  const h = d.getHours();
  if (diff === 0) return h < 6 ? '明け方' : h >= 15 ? '今夜' : '今日';
  if (diff === 1) return h < 6 ? (now.getHours() >= 15 ? '明け方' : '明日の明け方') : '明日';
  return md(d);
}

function moonTheme(c: CosmosData): ThemeItem {
  const m = c.moon;
  const lines = m.phaseKey === 'new' && m.daysToNew === 0
    ? [`新月の瞬間 ${hhmm(m.nextNew)}`, `満月まで ${daysLabel(m.daysToFull)}（${md(m.nextFull)}）`]
    : m.phaseKey === 'full' && m.daysToFull === 0
      ? [`満月の瞬間 ${hhmm(m.nextFull)}`, `新月まで ${daysLabel(m.daysToNew)}（${md(m.nextNew)}）`]
      : [`月齢 ${m.age.toFixed(1)}`, `満月まで ${daysLabel(m.daysToFull)}（${md(m.nextFull)}）`, `新月まで ${daysLabel(m.daysToNew)}（${md(m.nextNew)}）`];
  return {
    key: 'moon', label: '月のリズム', icon: MOON_ICON[m.phaseKey],
    headline: `今日は${m.phaseName}。${moonMessage(m.phaseKey)}`,
    lines,
    notes: [], needsLocation: false,
    special: m.phaseKey === 'new' || m.phaseKey === 'full',
  };
}

function sunTheme(c: CosmosData): ThemeItem {
  const s = c.sun;
  const today = s.startsToday;
  const lines: string[] = [];
  if (c.loc) lines.push(`日の出 ${hhmm(s.sunrise)}　日の入り ${hhmm(s.sunset)}（${c.loc.label}）`);
  lines.push(today ? `次は${s.next.name}（${md(s.next.date)}）` : `いまは${s.current.name}（${md(s.current.date)}から）`);
  const notes: string[] = [];
  if (today ? today.isSetsu : s.next.isSetsu) {
    notes.push(today ? `今日の節入りで、九星気学の月が切り替わります（${hhmm(today.date)}）` : `${s.next.name}は九星気学の月が切り替わる節入りです`);
  }
  return {
    key: 'sun', label: '太陽のリズム', icon: '☀️',
    headline: today ? `今日は${today.name}。${sekkiMessage(today.index)}` : `いまは${s.current.name}。次の${s.next.name}（${md(s.next.date)}）まで${daysLabel(s.daysToNext)}。`,
    lines, notes, needsLocation: c.loc === null, special: !!today,
  };
}

function spaceWeatherTheme(c: CosmosData): ThemeItem | null {
  const w = c.wx;
  if (!w) return null;
  const parts = [`地磁気 Kp ${w.kp ?? '–'}`];
  if (w.flareClass && /^[CMX]/.test(w.flareClass)) parts.push(`直近のフレア ${w.flareClass}`);
  if (w.gScale > 0) parts.push(`磁気嵐 G${w.gScale}`);
  return {
    key: 'spaceWeather', label: '宇宙天気', icon: w.level === 'storm' ? '🧲' : w.level === 'active' ? '🔆' : '🌞',
    headline: spaceWeatherMessage(w.level),
    lines: [parts.join('　') + '（NOAA）'],
    notes: auroraChance(w.kp, c.loc?.lat) ? ['北の低い空に、オーロラが見える可能性があります。'] : [],
    needsLocation: false, special: w.level === 'storm',
  };
}

function nightSkyTheme(c: CosmosData): ThemeItem | null {
  if (!c.loc && !c.meteor) return null;
  const lines: string[] = [];
  if (c.loc && c.planets.length) {
    lines.push(c.planets.slice(0, 3).map((p) => `${p.when}の${p.direction}の空に${p.name}`).join('、'));
  }
  if (c.loc && c.iss) {
    const p = c.iss;
    lines.push(`🛰️ ${dayWord(p.start, c.now)} ${hhmm(p.start)}ごろ、国際宇宙ステーションが${p.fromDir === p.toDir ? `${p.fromDir}の空を通ります` : `${p.fromDir}から${p.toDir}へ通ります`}（最大${p.maxElevation}°）`);
  }
  if (c.meteor) {
    const m = c.meteor;
    const bright = c.moon.age >= 10 && c.moon.age <= 19;
    lines.push(`☄️ ${m.inSeason ? `${m.name}が見ごろです（極大 ${md(m.peak)}）${bright ? '。月明かりが明るめの夜です' : ''}` : `${m.name}の極大まで あと${m.daysToPeak}日（${md(m.peak)}）`}`);
  }
  return {
    key: 'nightSky', label: '夜空', icon: '✨',
    headline: NIGHT_SKY_INVITE, lines, notes: [],
    needsLocation: c.loc === null, special: !!c.meteor?.inSeason,
  };
}

function earthTheme(c: CosmosData): ThemeItem {
  const e = c.earth;
  const lines: string[] = [];
  if (e.inDoyo && e.doyoEnd) lines.push(`土用明けまで ${daysLabel(Math.round((sodMs(e.doyoEnd) - sodMs(c.now)) / 86400000))}（${md(e.doyoEnd)}）`);
  else if (e.doyoStart && e.daysToDoyo != null) lines.push(`次の土用の入りまで ${daysLabel(e.daysToDoyo)}（${md(e.doyoStart)}）`);
  lines.push(`潮回りは${e.tide}。${tideMessage(e.tide)}（海辺の目安）`);
  const doyoStartsToday = e.inDoyo && e.doyoStart && sodMs(e.doyoStart) === sodMs(c.now);
  return {
    key: 'earth', label: '地球のリズム', icon: '🌏',
    headline: e.inDoyo && e.doyoSeason ? doyoMessage(e.doyoSeason) : `暦の上では${e.season}。${seasonMessage(e.season)}`,
    lines, notes: [], needsLocation: false, special: !!doyoStartsToday,
  };
}

export function buildThemes(c: CosmosData): ThemeItem[] {
  return [moonTheme(c), sunTheme(c), spaceWeatherTheme(c), nightSkyTheme(c), earthTheme(c)]
    .filter((t): t is ThemeItem => !!t);
}

/**
 * 今日の一つを選ぶ。
 * - 端末内で決まる節目（節入り・新月／満月・流星群の見ごろ・土用の入り）があればそれを優先
 *   （通信の結果で一日の途中に入れ替わらないよう、宇宙天気の磁気嵐は優先に含めない）
 * - なければ日付で五つを順番に回す。その日のテーマが出せなければ次のテーマへ
 */
export function pickDailyTheme(themes: ThemeItem[], now: Date, pending: ThemeKey[] = []): ThemeItem | null {
  const byKey = new Map(themes.map((t) => [t.key, t]));
  for (const k of ['sun', 'moon', 'nightSky', 'earth'] as ThemeKey[]) {
    const t = byKey.get(k);
    if (t && t.special) return t;
  }
  const dayNo = Math.floor(sodMs(now) / 86400000);
  const start = ((dayNo % THEME_ORDER.length) + THEME_ORDER.length) % THEME_ORDER.length;
  for (let i = 0; i < THEME_ORDER.length; i++) {
    const key = THEME_ORDER[(start + i) % THEME_ORDER.length];
    // まだ取得中のテーマに当たったら、確定するまで待つ（表示が途中で入れ替わらないように）
    if (pending.includes(key)) return null;
    const t = byKey.get(key);
    if (t) return t;
  }
  return themes[0];
}

/** まだデータが確定していないテーマ */
export function pendingThemes(c: CosmosData): ThemeKey[] {
  const p: ThemeKey[] = [];
  if (c.wx === undefined) p.push('spaceWeather');
  if (c.loc === undefined && !c.meteor) p.push('nightSky');
  return p;
}
